import { useState, useMemo } from 'react';
import {
  BookOpen, CheckCircle2, Sparkles,
  X, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import type { Usuario } from '../../types/models';
import {
  CATALOGO_BNCC,
  getCatalogoPorArea,
  type CatalogoItem,
} from './catalogo-bncc-data';
import './AssistenteBNCC.css';

// ── Tipos internos ──────────────────────────────────────────────────

interface ItemSelecionado {
  catalogo: CatalogoItem;
}

interface AssistenteBNCCProps {
  open: boolean;
  onClose: () => void;
  materiasExistentes: string[];
  seriesDisponíveis?: string[];   // mantido por compatibilidade
  professores?: Usuario[];        // mantido por compatibilidade
}

const ETAPA_LABELS = ['Ed. Infantil', 'Fund. I', 'Fund. II', 'Médio'];

// ── Componente principal ────────────────────────────────────────────

export function AssistenteBNCC({
  open, onClose, materiasExistentes,
}: AssistenteBNCCProps) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1);
  const [salvando, setSalvando] = useState(false);

  // Estado por etapa
  const [etapaFiltro, setEtapaFiltro] = useState<string>('Todas');
  const [selecionadas, setSelecionadas] = useState<Map<string, ItemSelecionado>>(new Map());

  const existentesLower = useMemo(
    () => new Set(materiasExistentes.map((n) => n.toLowerCase())),
    [materiasExistentes],
  );

  const catalogoFiltradoPorArea = useMemo(() => {
    const filtrado = etapaFiltro === 'Todas'
      ? CATALOGO_BNCC
      : CATALOGO_BNCC.filter((i) => i.etapasAplicaveis.includes(etapaFiltro));

    return Object.entries(
      filtrado.reduce((acc, item) => {
        if (!acc[item.areaDoCOnhecimento]) acc[item.areaDoCOnhecimento] = [];
        acc[item.areaDoCOnhecimento].push(item);
        return acc;
      }, {} as Record<string, CatalogoItem[]>),
    );
  }, [etapaFiltro]);

  // ── Helpers de seleção ────────────────────────────────────────────

  const toggleItem = (item: CatalogoItem) => {
    setSelecionadas((prev) => {
      const next = new Map(prev);
      if (next.has(item.nome)) {
        next.delete(item.nome);
      } else {
        next.set(item.nome, { catalogo: item });
      }
      return next;
    });
  };

  const toggleArea = (itens: CatalogoItem[]) => {
    const disponiveis = itens.filter((i) => !existentesLower.has(i.nome.toLowerCase()) || selecionadas.has(i.nome));
    const todosOn = disponiveis.every((i) => selecionadas.has(i.nome));
    setSelecionadas((prev) => {
      const next = new Map(prev);
      disponiveis.forEach((i) => {
        if (todosOn) next.delete(i.nome);
        else if (!next.has(i.nome)) {
          next.set(i.nome, { catalogo: i });
        }
      });
      return next;
    });
  };

  // ── Submit final ──────────────────────────────────────────────────

  const handleSalvar = async () => {
    if (!escolaId) return;
    setSalvando(true);
    try {
      const materias = Array.from(selecionadas.values()).map(({ catalogo }) => ({
        nome: catalogo.nome,
        codigo: catalogo.codigo,
        siglaBNCC: catalogo.siglaBNCC ?? undefined,
        areaDoCOnhecimento: catalogo.areaDoCOnhecimento,
        etapasAplicaveis: catalogo.etapasAplicaveis,
        obrigatoria: catalogo.obrigatoria,
        corVisual: catalogo.corVisual,
        origemBNCC: true,
      }));

      const { data: resultado } = await api.post(
        `/escolas/${escolaId}/materias/batch-bncc`,
        { materias },
      );

      const novas = resultado.criadas ?? 0;
      const atualizadas = resultado.atualizadas ?? 0;
      toast.success(
        `${novas} matéria(s) criada(s) e ${atualizadas} atualizada(s)!`,
        { duration: 4000 },
      );
      await queryClient.invalidateQueries({ queryKey: ['materias'] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      onClose();
    } catch {
      toast.error('Erro ao salvar matérias. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const qtdNovas = Array.from(selecionadas.keys())
    .filter((nome) => !existentesLower.has(nome.toLowerCase())).length;
  const qtdTotal = selecionadas.size;

  if (!open) return null;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="asst-overlay">
      <div className="asst-modal">

        {/* Header Responsivo e Compacto */}
        <div className="asst-header" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px' }}>
          {/* Linha superior: Ícone + Título + Fechar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <div className="asst-header-icon" style={{ width: 28, height: 28, borderRadius: 8 }}>
                <BookOpen size={14} />
              </div>
              <div className="asst-header-title" style={{ fontSize: '0.88rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Assistente de Catálogo BNCC
              </div>
            </div>

            <button className="asst-close" onClick={onClose} style={{ width: 26, height: 26, margin: 0, padding: 0 }} title="Fechar">
              <X size={15} />
            </button>
          </div>

          {/* Linha inferior: Stepper abaixo do título */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, width: '100%', paddingTop: 1 }}>
            {[['Selecionar', 'Selecionar Matérias'], ['Confirmar', 'Revisar e Confirmar']].map(([lblCurto, lblLongo], i) => {
              const n = i + 1;
              const done = step > n;
              const active = step === n;
              return (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 5 }} title={lblLongo}>
                  <div 
                    style={{ 
                      width: 18, 
                      height: 18, 
                      borderRadius: '50%', 
                      background: active ? '#ffffff' : done ? '#10b981' : 'rgba(255,255,255,0.2)', 
                      color: active ? '#1d4ed8' : done ? '#ffffff' : 'rgba(255,255,255,0.7)', 
                      fontSize: '0.62rem', 
                      fontWeight: 700, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}
                  >
                    {done ? <CheckCircle2 size={10} /> : n}
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: active ? 700 : 500, color: active ? '#ffffff' : 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
                    {lblCurto}
                  </span>
                  {n < 2 && <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', marginLeft: 4 }}>→</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="asst-body">

          {/* ── Etapa 1: Selecionar Matérias ─────────────────── */}
          {step === 1 && (
            <div>
              {/* Filtro de etapa */}
              <div className="asst-etapa-filter">
                {['Todas', ...ETAPA_LABELS].map((e) => (
                  <button
                    key={e}
                    className={`asst-etapa-btn ${etapaFiltro === e ? 'active' : ''}`}
                    onClick={() => setEtapaFiltro(e)}
                  >{e}</button>
                ))}
              </div>

              {/* Grupos por área */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px 20px', marginTop: 12 }}>
                {catalogoFiltradoPorArea.map(([area, itens]) => (
                  <div key={area} className="asst-area-grupo" style={{ margin: 0, display: 'flex', flexDirection: 'column', flex: '0 1 auto', minWidth: 200 }}>
                    <div className="asst-area-header" style={{ marginBottom: 8 }}>
                      <span className="asst-area-nome">{area}</span>
                    </div>
                    <div className="asst-chips">
                      {itens.map((item) => {
                        const jaExiste = existentesLower.has(item.nome.toLowerCase());
                        const sel = selecionadas.has(item.nome);
                        return (
                          <button
                            key={item.nome}
                            className={`asst-chip ${sel ? 'selected' : ''} ${jaExiste && !sel ? 'ja-existe' : ''}`}
                            style={sel ? { borderColor: item.corVisual, background: item.corVisual + '22' } : {}}
                            onClick={() => toggleItem(item)}
                            title={jaExiste ? 'Já cadastrada — clique para incluir/atualizar' : item.codigo}
                          >
                            <span
                              className="asst-chip-dot"
                              style={{ background: item.corVisual }}
                            />
                            {item.nome}
                            {item.obrigatoria && <span className="asst-chip-obrig" title="Obrigatória pela BNCC">★</span>}
                            {jaExiste && <span className="asst-chip-tag">existente</span>}
                            {sel && <CheckCircle2 size={11} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {qtdTotal === 0 && (
                <div className="asst-empty">
                  <Sparkles size={32} />
                  <p>Selecione as matérias que sua escola oferece</p>
                </div>
              )}
            </div>
          )}

          {/* ── Etapa 2: Revisão e Confirmação ──────────────────── */}
          {step === 2 && (
            <div className="asst-revisao">
              <div className="asst-revisao-card">
                <div className="asst-revisao-titulo">
                  <BookOpen size={16} /> {qtdTotal} matéria(s) selecionada(s)
                </div>
                <ul>
                  {Array.from(selecionadas.values()).map(({ catalogo }) => (
                    <li key={catalogo.nome} className="asst-revisao-item">
                      <span className="asst-chip-dot" style={{ background: catalogo.corVisual }} />
                      {catalogo.nome}
                      <span style={{ fontSize: '.72rem', color: '#64748b', marginLeft: 6 }}>
                        {catalogo.areaDoCOnhecimento}
                      </span>
                      {existentesLower.has(catalogo.nome.toLowerCase())
                        ? <span className="asst-tag-existente">atualizar</span>
                        : <span className="asst-tag-nova">nova</span>}
                    </li>
                  ))}
                </ul>
              </div>
              {qtdTotal === 0 && (
                <div className="asst-warning">
                  <AlertTriangle size={14} /> Nenhuma matéria selecionada.
                </div>
              )}
              <div style={{ fontSize: '.82rem', color: '#64748b', marginTop: 12, lineHeight: 1.5 }}>
                As matérias serão cadastradas na escola. Professores e carga horária podem ser
                configurados individualmente depois, em cada matéria.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="asst-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px' }}>
          {step > 1 ? (
            <button
              className="asst-btn-voltar"
              onClick={() => setStep((s) => s - 1)}
              disabled={salvando}
            >
              ← Voltar
            </button>
          ) : (
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-main, #334155)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#2563eb' }} />
              {qtdTotal} selecionada(s) · {qtdNovas} nova(s)
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            {step < 2 ? (
              <button
                className="asst-btn-proximo"
                onClick={() => setStep(2)}
                disabled={qtdTotal === 0}
              >
                Revisar ({qtdTotal}) →
              </button>
            ) : (
              <button
                className="asst-btn-salvar"
                onClick={handleSalvar}
                disabled={salvando || qtdTotal === 0}
              >
                {salvando ? 'Salvando...' : `Confirmar e Salvar (${qtdTotal})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
